import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface Region {
  code: string;
  name: string;
  flag: string;
  compliance: string;
  color: string;
}

// Available regions matching seed data
export const REGIONS: Region[] = [
  { code: 'ALL', name: 'All Regions', flag: '🌍', compliance: 'All', color: '#6366f1' },
  { code: 'IN', name: 'India', flag: '🇮🇳', compliance: 'DPDP', color: '#f97316' },
  { code: 'EU', name: 'European Union', flag: '🇪🇺', compliance: 'GDPR', color: '#3b82f6' },
  { code: 'US', name: 'United States', flag: '🇺🇸', compliance: 'CCPA', color: '#10b981' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', compliance: 'PDPA', color: '#8b5cf6' },
];

interface RegionContextType {
  selectedRegion: Region;
  setSelectedRegion: (region: Region) => void;
  regions: Region[];
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

const STORAGE_KEY = 'selected_region';

export const RegionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedRegion, setSelectedRegionState] = useState<Region>(() => {
    // Load from localStorage on init
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const found = REGIONS.find(r => r.code === parsed.code);
        if (found) return found;
      }
    } catch {
      // ignore
    }
    return REGIONS[0]; // Default to "All Regions"
  });

  const setSelectedRegion = (region: Region) => {
    setSelectedRegionState(region);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(region));
  };

  return (
    <RegionContext.Provider value={{ selectedRegion, setSelectedRegion, regions: REGIONS }}>
      {children}
    </RegionContext.Provider>
  );
};

export const useRegion = (): RegionContextType => {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  return context;
};

export default RegionContext;
